import { Link } from 'react-router-dom';
import { CheckCircle, Building2, Home, GraduationCap, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PLANS = [
  {
    icon: Home,
    tag: 'FAMILIES & SOLO',
    title: 'Families',
    testid: 'price-family',
    accent: 'zap-yellow',
    to: '/login',
    cta: 'Start Playing Free',
    features: [
      'Unlimited children',
      'Personal island for each child',
      'Full character customisation',
      'All zones & stages',
      'Global leaderboard',
      'Parent dashboard',
    ],
  },
  {
    icon: Building2,
    tag: 'SCHOOLS',
    title: 'Schools',
    testid: 'price-school',
    accent: 'bot-cyan',
    to: '/trial',
    cta: 'Get Started Free',
    featured: true,
    features: [
      'Unlimited pupils & classes',
      'Teacher dashboard',
      'Full typing curriculum',
      'Year group competitions',
      'SENCO reporting & exports',
      'No card required',
    ],
  },
  {
    icon: GraduationCap,
    tag: 'TUTORS & HOME ED',
    title: 'Tutors',
    testid: 'price-teacher',
    accent: 'zap-yellow',
    to: '/login',
    cta: 'Get Started Free',
    features: [
      'Unlimited pupil accounts',
      'Full teacher dashboard',
      'Custom word lists',
      'Assignment scheduling',
      'Progress tracking',
      'SENCO reporting',
    ],
  },
];

const FAQS = [
  {
    q: 'How much does TypeTown cost?',
    a: 'Nothing! TypeTown is completely free for everyone - schools, families, tutors and home educators. There are no subscriptions and no hidden fees.',
  },
  {
    q: 'Do I need a credit card to sign up?',
    a: 'No. You never need to enter any payment details. Just create an account and start playing straight away.',
  },
  {
    q: 'Is everything really included for free?',
    a: 'Yes - the full island, all stages, character customisation, the leaderboard, teacher/parent dashboards and SENCO reporting are all free, with full features from day one.',
  },
  {
    q: 'Are there any in-app purchases with real money?',
    a: 'No real-money purchases at all. Children earn virtual coins by completing typing tasks, which they can spend on fun character and town items in the game.',
  },
  {
    q: 'Is it safe for children?',
    a: 'Absolutely. TypeTown is GDPR compliant, has no adverts and no real-money purchases, so there are no surprise bills or distractions.',
  },
];

const ACCENT = {
  'bot-cyan': { text: 'text-bot-cyan', border: 'border-bot-cyan', hover: 'hover:bg-bot-cyan/10' },
  'zap-yellow': { text: 'text-zap-yellow', border: 'border-zap-yellow', hover: 'hover:bg-zap-yellow/10' },
};

const PricingPage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-zap-yellow/15 border border-zap-yellow/40 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-zap-yellow" />
            <span className="font-mono text-xs text-zap-yellow">FREE FOREVER</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-screen-white mb-6" data-testid="pricing-headline">
            TypeTown is <span className="text-zap-yellow">100% Free</span>
          </h1>
          <p className="font-body text-lg text-screen-white/80 max-w-2xl mx-auto">
            No fees. No subscriptions. No card required. Free for schools, families and tutors - with every feature unlocked from day one.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PLANS.map((plan, i) => (
            <div
              key={i}
              data-testid={plan.testid}
              className={`bg-dark-panel rounded-xl p-6 relative ${plan.featured ? 'border-2 border-bot-cyan md:-translate-y-4' : 'border-2 border-mid-panel'}`}
            >
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-bot-cyan text-deep-space font-mono text-xs px-3 py-1 rounded-full">MOST POPULAR</div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <plan.icon className={`w-5 h-5 ${ACCENT[plan.accent].text}`} />
                <span className={`font-mono text-xs ${ACCENT[plan.accent].text}`}>{plan.tag}</span>
              </div>
              <h3 className="font-display text-2xl text-screen-white mb-2">{plan.title}</h3>
              <div className="mb-4 flex items-baseline gap-2">
                <span className="font-display text-4xl text-zap-yellow">£0</span>
                <span className="font-body text-screen-white/60 text-sm">forever</span>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-2 text-screen-white/70">
                    <CheckCircle className={`w-4 h-4 ${ACCENT[plan.accent].text} flex-shrink-0`} />
                    <span className="font-body text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={plan.to}
                className={`font-display uppercase tracking-wider text-sm px-4 py-2 rounded-sm block text-center transition-colors ${
                  plan.featured
                    ? 'bg-zap-yellow text-deep-space hover:bg-zap-yellow/90 shadow-[0_4px_0_0_#B2A000] hover:shadow-[0_2px_0_0_#B2A000] hover:translate-y-[2px]'
                    : `bg-transparent ${ACCENT[plan.accent].text} border-2 ${ACCENT[plan.accent].border} ${ACCENT[plan.accent].hover}`
                }`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Reassurance banner */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-bot-cyan/20 to-zap-yellow/20 border border-bot-cyan/30 rounded-xl p-6 text-center">
          <p className="font-display text-xl text-screen-white mb-2">
            Everything is free - no card, no catch.
          </p>
          <p className="font-body text-screen-white/70 text-sm">
            Children earn virtual coins by typing, then spend them on fun in-game items. No real-money purchases, ever.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl text-screen-white text-center mb-12">Common Questions</h2>
          <div className="space-y-6">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-dark-panel border border-mid-panel rounded-xl p-6" data-testid={`faq-${i}`}>
                <h3 className="font-display text-lg text-screen-white mb-2">{faq.q}</h3>
                <p className="font-body text-screen-white/70">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl text-screen-white mb-4">Ready to Start?</h2>
          <p className="font-body text-screen-white/60 mb-8">Join thousands of schools and families already playing TypeTown - free.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/trial"
              data-testid="pricing-school-cta"
              className="font-display uppercase tracking-wider bg-zap-yellow text-deep-space px-10 py-4 text-xl rounded-sm hover:translate-y-1 hover:shadow-none shadow-[0_4px_0_0_#B2A000] transition-all"
            >
              Schools - Get Started
            </Link>
            <Link
              to="/login"
              data-testid="pricing-solo-cta"
              className="font-display uppercase tracking-wider bg-transparent text-bot-cyan border-2 border-bot-cyan px-10 py-4 text-xl rounded-sm hover:bg-bot-cyan/10 transition-all"
            >
              Play Free
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
